import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, TextField, Button, Checkbox, FormControlLabel, CircularProgress, Typography, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import useEducationForm from '../../hooks/useEducationForm';
import { useQueryClient } from 'react-query';

const EducationForm = ({ onCancel, id, cvId }) => {
    const queryClient = useQueryClient();
    const { register, handleSubmit, watch, reset, formState: { errors }, setValue } = useForm({
        defaultValues: {
            level: '',
            field: '',
            location: '',
            start_date: '',
            end_date: '',
            description: '',
            isCurrentlyStudying: false
        }
    });

    const { isLoading, error, data, saveEducation } = useEducationForm(id);

    useEffect(() => {
        if (data) {
            // Map backend field names to form field names
            const mappedData = {
                level: data.level || '',
                field: data.field || '',
                location: data.location || '',
                start_date: data.start_date ? new Date(data.start_date).toISOString().split('T')[0] : '',
                end_date: data.end_date ? new Date(data.end_date).toISOString().split('T')[0] : '',
                description: data.description || '',
                isCurrentlyStudying: !data.end_date
            };
            reset(mappedData);
        }
    }, [data, reset]);

    const isCurrentlyStudying = watch('isCurrentlyStudying');
    const level = watch('level');

    const handleFormSubmit = async (formData) => {
        try {
            // Map form field names to backend field names
            const backendData = {
                level: formData.level,
                field: formData.field,
                location: formData.location,
                start_date: formData.start_date,
                end_date: formData.isCurrentlyStudying ? null : formData.end_date,
                description: formData.description,
                cv: cvId // Include the CV ID when saving
            };
            
            await saveEducation(backendData);
            // Invalidate and refetch queries - use null if cvId is undefined to match the query key
            await queryClient.invalidateQueries(['educations', cvId || null]);
            await queryClient.refetchQueries(['educations', cvId || null]);
            // Also invalidate all education queries to be safe
            await queryClient.invalidateQueries(['educations']);
            onCancel();
        } catch (err) {
            // Error is handled by the hook and displayed in the form
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Box className="space-y-4">
                {error && (
                    <Typography color="error" className="mb-4">
                        {error}
                    </Typography>
                )}

                <FormControl fullWidth error={!!errors.level} className="mb-2">
                    <InputLabel id="education-level-label">Education Level</InputLabel>
                    <Select
                        labelId="education-level-label"
                        id="education-level"
                        value={level}
                        label="Education Level"
                        onChange={(e) => setValue('level', e.target.value)}
                        {...register('level', { required: 'Education level is required' })}
                    >
                        <MenuItem value="High School">High School</MenuItem>
                        <MenuItem value="Associate Degree">Associate Degree</MenuItem>
                        <MenuItem value="Bachelor's Degree">Bachelor's Degree</MenuItem>
                        <MenuItem value="Master's Degree">Master's Degree</MenuItem>
                        <MenuItem value="Doctorate">Doctorate</MenuItem>
                        <MenuItem value="Certificate">Certificate</MenuItem>
                        <MenuItem value="Diploma">Diploma</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    {errors.level && (
                        <Typography color="error" variant="caption">
                            {errors.level.message}
                        </Typography>
                    )}
                </FormControl>

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Field of Study"
                    {...register('field', { required: 'Field of study is required' })}
                    error={!!errors.field}
                    helperText={errors.field?.message}
                />

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Institution/Location"
                    {...register('location', { required: 'Institution/Location is required' })}
                    error={!!errors.location}
                    helperText={errors.location?.message}
                />

                <TextField
                    fullWidth
                    className='mb-2'
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    {...register('start_date', { required: 'Start date is required' })}
                    error={!!errors.start_date}
                    helperText={errors.start_date?.message}
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            {...register('isCurrentlyStudying')}
                        />
                    }
                    label="I am currently studying here"
                />

                {!isCurrentlyStudying && (
                    <TextField
                        fullWidth
                        type="date"
                        className='mb-2'
                        label="End Date"
                        InputLabelProps={{ shrink: true }}
                        {...register('end_date', { 
                            required: !isCurrentlyStudying ? 'End date is required' : false 
                        })}
                        error={!!errors.end_date}
                        helperText={errors.end_date?.message}
                    />
                )}

                <TextField
                    fullWidth
                    multiline
                    rows={1}
                    className='mb-2'
                    label="Description"
                    {...register('description')}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                />

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {id ? 'Update' : 'Add'} Education
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default EducationForm; 